import { computed, ref } from '@vue/composition-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { TariffRegion, ITariffRegionData } from '@/models';
import { MaybeRef } from '@tanstack/vue-query/build/lib/types';
import providerInstance from '@/services/provider';

export function useFetchTariffRegions() {
  const queryKey = ref(['tariffRegions']);

  const { isLoading, isError, error, data: tariffRegions } = useQuery({
    queryKey: queryKey,
    queryFn: () => providerInstance.tariff.fetchTariffRegions(),
    enabled: true,
    select: data => {
      return data.map(tariffRegion => new TariffRegion(tariffRegion));
    }
  });

  return { queryKey, tariffRegions, error, isError, isLoading };
}

export function useFetchTariffRegion(id: MaybeRef<number>) {
  const tariffRegionIdRef = ref(id);

  const queryKey = ref(['tariffRegions', tariffRegionIdRef]);

  const { data: tariffRegion, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(tariffRegionIdRef.value)),
    queryFn: () =>
      providerInstance.tariff.fetchTariffRegion(tariffRegionIdRef.value),
    select: data => new TariffRegion(data)
  });

  return { tariffRegion, isLoading, error, queryKey };
}

export function useCreateTariffRegion() {
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-tariffRegion'],
    mutationFn: (tariffRegion: ITariffRegionData) =>
      providerInstance.tariff.createTariffRegion(tariffRegion),

    onSuccess: data => {
      queryClient.setQueryData(['tariffRegions', data.id], data);
      queryClient.invalidateQueries({ queryKey: ref(['tariffRegions']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateTariffRegion() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-tariffRegion'],
    mutationFn: (tariffRegion: ITariffRegionData) =>
      providerInstance.tariff.updateTariffRegion(tariffRegion),

    onSuccess: data => {
      queryClient.setQueryData(['tariffRegions', data.id], data);
      queryClient.invalidateQueries({
        queryKey: ref(['tariffRegions', data.id])
      });
      queryClient.invalidateQueries({ queryKey: ref(['tariffRegions']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteTariffRegion() {
  const mutationKey = ref(['delete-tariffRegion']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) => providerInstance.tariff.deleteTariffRegion(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['tariffRegions']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}
